import { useMemo, useState, useEffect, useCallback } from 'react';
import { Card, Row, Col, Button, Form, Badge, Alert } from 'react-bootstrap';
import PropTypes from 'prop-types';
import { extractFillInAcceptedAnswers } from '@utils/fillInAnswerMatching';

function shuffleArray(items) {
  const copy = [...items];
  for (let i = copy.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [copy[i], copy[j]] = [copy[j], copy[i]];
  }
  return copy;
}

function normalizeOptionLabel(option) {
  if (option == null) return '—';
  if (typeof option === 'string' || typeof option === 'number') return String(option);
  return (
    option.text ??
    option.label ??
    option.value ??
    option.id ??
    option.key ??
    option.name ??
    String(option)
  );
}

function resolveLabel(items, value) {
  if (!items || !Array.isArray(items)) {
    return value != null ? String(value) : '—';
  }

  if (typeof value === 'number' && items[value] != null) {
    return normalizeOptionLabel(items[value]);
  }

  const match = items.find((item) => {
    if (item == null) return false;
    if (item === value) return true;
    if (typeof item === 'object') {
      return item.id === value || item.value === value || item.key === value || item.text === value;
    }
    return false;
  });

  return match != null ? normalizeOptionLabel(match) : (value != null ? String(value) : '—');
}

function formatAnswer(question) {
  if (!question) return '—';

  const { type, correctAnswer, options, leftItems, rightItems, items } = question;

  switch (type) {
    case 'multiple-choice':
    case 'true-false':
      return resolveLabel(options, correctAnswer);
    case 'fill-in': {
      const answers = extractFillInAcceptedAnswers(correctAnswer);
      return answers.length > 0 ? answers.join(', ') : '—';
    }
    case 'multi-select': {
      const answers = Array.isArray(correctAnswer) ? correctAnswer : [correctAnswer];
      return answers.map((answer) => resolveLabel(options, answer)).join(', ');
    }
    case 'matching': {
      if (!correctAnswer || typeof correctAnswer !== 'object') return '—';
      const pairs = Object.entries(correctAnswer).map(([left, right]) => {
        const leftLabel = resolveLabel(leftItems, left);
        const rightLabel = resolveLabel(rightItems, right);
        return `${leftLabel} → ${rightLabel}`;
      });
      return pairs.length > 0 ? pairs.join(' | ') : '—';
    }
    case 'drag-drop': {
      const order = Array.isArray(correctAnswer) ? correctAnswer : [];
      return order.map((entry) => resolveLabel(items, entry)).join(' → ');
    }
    default:
      return correctAnswer != null ? String(correctAnswer) : '—';
  }
}

export function FlashcardSession({ course, progress }) {
  const [selectedSectionIds, setSelectedSectionIds] = useState([]);
  const [selectedTags, setSelectedTags] = useState([]);
  const [questionQueue, setQuestionQueue] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [shuffleSeed, setShuffleSeed] = useState(0);
  const [showAnswer, setShowAnswer] = useState(false);

  const questionPoolScope = course?.metadata?.questionPoolScope || 'unlocked';

  const baseSections = useMemo(() => {
    if (!course?.sections?.length) return [];
    if (questionPoolScope === 'all') {
      return course.sections;
    }
    const unlockedSectionIds = progress?.unlockedSections || [];
    const unlocked = course.sections.filter((section) => unlockedSectionIds.includes(section.id));
    return unlocked.length > 0 ? unlocked : [course.sections[0]];
  }, [course, questionPoolScope, progress?.unlockedSections]);

  useEffect(() => {
    setSelectedSectionIds(baseSections.map((section) => section.id));
  }, [baseSections]);

  const availableTags = useMemo(() => {
    const tagSet = new Set();
    baseSections.forEach((section) => {
      (section.questions || []).forEach((question) => {
        (question.tags || []).forEach((tag) => tagSet.add(tag));
      });
    });
    return Array.from(tagSet).sort((a, b) => a.localeCompare(b));
  }, [baseSections]);

  useEffect(() => {
    setSelectedTags((prev) => prev.filter((tag) => availableTags.includes(tag)));
  }, [availableTags]);

  const filteredQuestions = useMemo(() => {
    const selectedSections = baseSections.filter((section) => selectedSectionIds.includes(section.id));
    const questions = selectedSections.flatMap((section) =>
      (section.questions || []).map((question) => ({
        ...question,
        sectionId: question.sectionId || section.id,
      }))
    );

    if (selectedTags.length === 0) {
      return questions;
    }

    return questions.filter((question) =>
      (question.tags || []).some((tag) => selectedTags.includes(tag))
    );
  }, [baseSections, selectedSectionIds, selectedTags]);

  useEffect(() => {
    setQuestionQueue(shuffleArray(filteredQuestions));
    setCurrentIndex(0);
    setShowAnswer(false);
  }, [filteredQuestions, shuffleSeed]);

  const currentQuestion = questionQueue[currentIndex] || null;
  const hasQuestions = questionQueue.length > 0;
  const isComplete = hasQuestions && currentIndex >= questionQueue.length;

  useEffect(() => {
    setShowAnswer(false);
  }, [currentQuestion?.id]);

  const handleNextQuestion = useCallback(() => {
    setShowAnswer(false);
    setCurrentIndex((prev) => Math.min(prev + 1, questionQueue.length));
  }, [questionQueue.length]);

  const handleReshuffle = () => {
    setShuffleSeed((prev) => prev + 1);
  };

  const handleToggleSection = (sectionId) => {
    setSelectedSectionIds((prev) => {
      if (prev.includes(sectionId)) {
        return prev.filter((id) => id !== sectionId);
      }
      return [...prev, sectionId];
    });
  };

  const handleToggleTag = (tag) => {
    setSelectedTags((prev) => {
      if (prev.includes(tag)) {
        return prev.filter((t) => t !== tag);
      }
      return [...prev, tag];
    });
  };

  const handleSelectAllSections = () => {
    setSelectedSectionIds(baseSections.map((section) => section.id));
  };

  const handleClearSections = () => {
    setSelectedSectionIds([]);
  };

  const getSectionTitle = (sectionId) => {
    return course?.sections?.find((section) => section.id === sectionId)?.title || 'Section';
  };

  if (!course || !progress) {
    return (
      <Card className="text-center p-5">
        <p className="text-muted mb-0">Loading flashcards...</p>
      </Card>
    );
  }

  return (
    <Row className="g-3">
      <Col lg={4}>
        <Card className="mb-3">
          <Card.Body>
            <div className="d-flex align-items-center justify-content-between mb-2">
              <h6 className="mb-0">Flashcard Pool</h6>
              <Badge bg={questionPoolScope === 'all' ? 'info' : 'success'}>
                {questionPoolScope === 'all' ? 'All Sections' : 'Unlocked Sections'}
              </Badge>
            </div>
            <p className="text-muted small mb-3">
              Choose which sections to review and filter by tags. Cards are shown in a random order.
            </p>
            <div className="d-flex gap-2 mb-3">
              <Button variant="outline-secondary" size="sm" onClick={handleSelectAllSections}>
                Select all
              </Button>
              <Button variant="outline-secondary" size="sm" onClick={handleClearSections}>
                Clear
              </Button>
            </div>
            <div className="question-only-section-list">
              {baseSections.map((section) => {
                const isUnlocked = progress.unlockedSections?.includes(section.id);
                return (
                  <Form.Check
                    key={section.id}
                    type="checkbox"
                    id={`flashcard-section-${section.id}`}
                    className="mb-2"
                    checked={selectedSectionIds.includes(section.id)}
                    onChange={() => handleToggleSection(section.id)}
                    label={
                      <span>
                        {section.title}
                        {!isUnlocked && questionPoolScope === 'all' && (
                          <Badge bg="secondary" className="ms-2">Locked</Badge>
                        )}
                      </span>
                    }
                  />
                );
              })}
              {baseSections.length === 0 && (
                <Alert variant="warning" className="mt-2">
                  No sections available for this flashcard pool.
                </Alert>
              )}
            </div>
          </Card.Body>
        </Card>

        <Card>
          <Card.Body>
            <h6 className="mb-2">Filter by Tags</h6>
            {availableTags.length === 0 ? (
              <p className="text-muted small mb-0">No tags found in the selected sections.</p>
            ) : (
              <div className="d-flex flex-wrap gap-2">
                {availableTags.map((tag) => (
                  <Button
                    key={tag}
                    size="sm"
                    variant={selectedTags.includes(tag) ? 'primary' : 'outline-primary'}
                    onClick={() => handleToggleTag(tag)}
                  >
                    {tag}
                  </Button>
                ))}
              </div>
            )}
            {selectedTags.length === 0 && availableTags.length > 0 && (
              <small className="text-muted d-block mt-2">All tags selected</small>
            )}
          </Card.Body>
        </Card>
      </Col>

      <Col lg={8}>
        <Card className="mb-3">
          <Card.Body className="d-flex flex-wrap justify-content-between align-items-center gap-2">
            <div>
              <h5 className="mb-1">Flashcards</h5>
              <small className="text-muted">
                {hasQuestions ? `${Math.min(currentIndex + 1, questionQueue.length)} / ${questionQueue.length}` : '0 / 0'}
              </small>
            </div>
            <div className="d-flex gap-2">
              <Button variant="outline-primary" onClick={handleReshuffle} disabled={!hasQuestions}>
                Reshuffle
              </Button>
              <Button variant="outline-secondary" onClick={() => { setCurrentIndex(0); setShowAnswer(false); }} disabled={!hasQuestions}>
                Restart
              </Button>
            </div>
          </Card.Body>
        </Card>

        {!hasQuestions && (
          <Card className="text-center p-5">
            <p className="text-muted mb-0">No questions match the selected filters.</p>
          </Card>
        )}

        {isComplete && hasQuestions && (
          <Card className="text-center p-5">
            <h5 className="mb-2">You reached the end of the queue.</h5>
            <p className="text-muted">Reshuffle to keep reviewing.</p>
            <Button variant="primary" onClick={handleReshuffle}>
              Shuffle Again
            </Button>
          </Card>
        )}

        {currentQuestion && !isComplete && (
          <Card className="shadow-sm">
            <Card.Body>
              {currentQuestion.tags?.length > 0 && (
                <div className="d-flex flex-wrap gap-2 mb-3">
                  {currentQuestion.tags.map((tag) => (
                    <Badge key={tag} bg="light" text="dark">
                      {tag}
                    </Badge>
                  ))}
                </div>
              )}

              <div className="mb-3">
                <h6 className="text-muted mb-1">{getSectionTitle(currentQuestion.sectionId)}</h6>
                <h4 className="mb-0">{currentQuestion.text}</h4>
              </div>

              {showAnswer ? (
                <Card className="bg-light border-0 mb-3">
                  <Card.Body>
                    <h6 className="text-muted">Answer</h6>
                    <p className="mb-0">{formatAnswer(currentQuestion)}</p>
                    {currentQuestion.explanation && (
                      <div className="mt-3">
                        <h6 className="text-muted">Explanation</h6>
                        <p className="mb-0">{currentQuestion.explanation}</p>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              ) : null}

              <div className="d-flex flex-wrap gap-2">
                <Button variant="outline-primary" onClick={() => setShowAnswer((prev) => !prev)}>
                  {showAnswer ? 'Hide the answer' : 'Show me the answer'}
                </Button>
                <Button variant="success" onClick={handleNextQuestion}>
                  I got it
                </Button>
              </div>
            </Card.Body>
          </Card>
        )}
      </Col>
    </Row>
  );
}

FlashcardSession.propTypes = {
  course: PropTypes.object,
  progress: PropTypes.object,
};
